package com.karawek.core.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import com.karawek.core.ui.theme.CoreLightColorScheme
import com.karawek.core.ui.theme.CoreDarkColorScheme

@Composable
fun CoreScaffold(
    title: String? = null,
    subtitle: String? = null,
    onBack: (() -> Unit)? = null,
    actionText: String? = null,
    onAction: (() -> Unit)? = null,
    scrollable: Boolean = true,
    modifier: Modifier = Modifier,
    footer: (@Composable () -> Unit)? = null,
    onAppear: (() -> Unit)? = null,
    onAppearKey: Any? = Unit,
    content: @Composable () -> Unit,
) {
    LaunchedEffect(onAppearKey) {
        onAppear?.invoke()
    }

    val topInset = WindowInsets.statusBars.asPaddingValues().calculateTopPadding()

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .imePadding()
    ) {
        Spacer(modifier = Modifier.height(topInset))

        if (title != null || onBack != null) {
            CoreHeader(
                title = title,
                showBack = onBack != null,
                onBack = onBack,
                actionText = actionText,
                onAction = onAction,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = CoreSpacing.sm),
            )
        }

        if (subtitle != null) {
            Text(
                text = subtitle,
                style = CoreTypography.bodySecondary,
                color = CoreTheme.colors.textSecondary,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = CoreSpacing.md),
            )
        }

        val contentModifier = Modifier
            .weight(1f)
            .fillMaxWidth()
            .then(if (scrollable) Modifier.verticalScroll(rememberScrollState()) else Modifier)
            .padding(all = CoreSpacing.md)

        Box(modifier = contentModifier) {
            content()
        }

        if (footer != null) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(all = CoreSpacing.md)
            ) {
                footer()
            }
        }
    }
}

@Preview
@Composable
private fun CoreScaffoldPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    CoreTheme(lightColors = CoreLightColorScheme, darkColors = CoreDarkColorScheme, themeMode = themeMode) {
        CoreScaffold(
            title = "Screen Title",
            subtitle = "This is a subtitle describing the screen",
        ) {
            Text("Content goes here")
        }
    }
}

@Preview
@Composable
private fun CoreScaffoldWithFooterPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    CoreTheme(lightColors = CoreLightColorScheme, darkColors = CoreDarkColorScheme, themeMode = themeMode) {
        CoreScaffold(
            title = "Screen Title",
            subtitle = "This is a subtitle describing the screen",
            footer = { Text("Footer content") },
        ) {
            Text("Content goes here")
        }
    }
}

@Preview
@Composable
private fun CoreScaffoldWithoutHeaderPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    CoreTheme(lightColors = CoreLightColorScheme, darkColors = CoreDarkColorScheme, themeMode = themeMode) {
        CoreScaffold {
            Text("Content goes here")
        }
    }
}

@Preview
@Composable
private fun CoreScaffoldWithActionPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    CoreTheme(lightColors = CoreLightColorScheme, darkColors = CoreDarkColorScheme, themeMode = themeMode) {
        CoreScaffold(
            title = "Your Invitations",
            subtitle = "These are requests to connect. Nothing is shared unless you accept.",
            actionText = "Skip",
            onAction = {},
        ) {
            Text("Content goes here")
        }
    }
}
